import { pharmacyInventoryApi } from "./inventory-api";
import {
  getProductPrice,
  setProductPrice,
  clearReservationsForLines,
} from "../pharmacy/pharmacy-store";
import type { InvoiceLine, StockProduct } from "../pharmacy/types";
import { isApiOfflineError, isInsufficientStockError } from "./inventory-errors";

export { isApiOfflineError, isInsufficientStockError };

/** Référence ISC stable par ligne de facture brouillon */
export function lineReservationRef(lineId: string): string {
  return `pharmacy:line:${lineId}`;
}

export function resolveProductPrice(product: StockProduct): number | null {
  const local = getProductPrice(product.id);
  if (local !== null) return local;
  return product.sellingPrice;
}

export async function persistProductPrice(
  productId: string,
  price: number,
): Promise<void> {
  setProductPrice(productId, price);
  try {
    await pharmacyInventoryApi.updateSellingPrice(productId, price);
  } catch {
    /* localStorage conservé — sync ISC au prochain succès API */
  }
}

export async function reserveInvoiceLine(
  line: Pick<InvoiceLine, "id" | "productId">,
  storeId: string,
  quantity: number,
): Promise<void> {
  await pharmacyInventoryApi.upsertReservation({
    productId: line.productId,
    storeId,
    quantity,
    externalRef: lineReservationRef(line.id),
  });
}

export function prescriptionReservationRef(
  prescriptionId: string,
  lineId: string,
): string {
  return `pharmacy:rx:${prescriptionId}:${lineId}`;
}

export async function reservePrescriptionLine(
  prescriptionId: string,
  line: { id: string; productId: string; quantity: number },
  storeId: string,
): Promise<void> {
  await pharmacyInventoryApi.upsertReservation({
    productId: line.productId,
    storeId,
    quantity: line.quantity,
    externalRef: prescriptionReservationRef(prescriptionId, line.id),
  });
}

export async function releasePrescriptionLine(
  prescriptionId: string,
  line: { id: string },
): Promise<void> {
  await pharmacyInventoryApi.releaseReservation(
    prescriptionReservationRef(prescriptionId, line.id),
  );
}

/** Sortie stock ordonnance — consomme les réservations par ligne. */
export async function issuePrescriptionLinesAtomically(
  prescriptionId: string,
  lines: Array<{ id: string; productId: string; quantity: number }>,
  storeId: string,
): Promise<void> {
  if (lines.length === 0) return;
  await pharmacyInventoryApi.postIssueBatch({
    reference: `RX-${prescriptionId.slice(-8)}`,
    lines: lines.map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
      sourceStoreId: storeId,
      externalRef: prescriptionReservationRef(prescriptionId, line.id),
    })),
  });
}

export async function releaseInvoiceLine(
  line: Pick<InvoiceLine, "id" | "productId" | "quantity">,
): Promise<void> {
  await pharmacyInventoryApi.releaseReservation(lineReservationRef(line.id));
}

export async function releaseInvoiceLines(lines: InvoiceLine[]): Promise<void> {
  await Promise.all(lines.map((line) => releaseInvoiceLine(line)));
}

/** Sortie stock atomique (un seul mouvement ISC) — consomme les réservations par ligne. */
export async function issueInvoiceLinesAtomically(
  lines: InvoiceLine[],
  storeId: string,
  invoiceNumber: string,
): Promise<void> {
  if (lines.length === 0) return;
  await pharmacyInventoryApi.postIssueBatch({
    reference: `PHARMACY-${invoiceNumber}`,
    lines: lines.map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
      sourceStoreId: storeId,
      externalRef: lineReservationRef(line.id),
    })),
  });
  clearReservationsForLines(lines);
}
